<template>
  <b-card>
    <b-row>
      <b-col>
        <h1 class="mb-2">
          Create a new customer
        </h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
          <b-form
            class="d-flex flex-column"
            @submit="onSubmit"
          >
            <b-form-group
              label="Name:"
              label-for="input-name"
            >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  placeholder="Enter customer name"
                  required
                />
                <small class="text-danger" v-if="v$.form.name.$error">Required</small>
            </b-form-group>

            <b-form-group
              label="Picture:"
              label-for="input-picture"
            >
              <b-form-file
                id="input-picture"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                @change="onPictureChange"
              />
              <b-img
                v-if="picturePreview"
                thumbnail
                rounded="circle"
                fluid
                :src="picturePreview"
                class="mt-1"
                :style="{ width: '120px', height: '120px', objectFit: 'cover' }"
                alt=""
              />
            </b-form-group>

            <b-form-group
              label="Email:"
              label-for="input-email"
            >
                <b-form-input
                  id="input-email"
                  v-model="form.email"
                  placeholder="Enter customer email"
                  type="email"
                  required
                />
                <small class="text-danger" v-if="v$.form.email.$error">Required</small>
            </b-form-group>

            <b-form-group
              label="Phone number (optional):"
              label-for="input-phone"
              class="mb-2"
            >
                <PhoneNumberInput
                  id="input-phone"
                  v-model:dial="customerDial"
                  v-model:number="customerNumber"
                />
            </b-form-group>

            <b-form-group
                label="Card last 4 digits"
                label-for="card_last_4"
            >
              <b-form-input
                  id="card_last_4"
                  v-model="form.card_last_4"
                  placeholder="Enter card last 4 digits"
                  type="number"
                  :min="1"
                  :max="9999"
              />
            </b-form-group>


            <b-form-group>
              <b-form-textarea
                  v-model="form.description"
                  placeholder="Description"
                  rows="3"
                  max-rows="6"
              />
            </b-form-group>
            <b-button
              type="submit"
              variant="primary"
              class="align-self-end w-xs-100 w-25"
              :disabled="isCreateLoading"
            >
              <b-spinner
                v-if="isCreateLoading"
                label="Spinning"
                small
              />
              <span v-else>
                Create
              </span>
            </b-button>
          </b-form>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BForm,
  BFormFile,
  BImg,
  BSpinner,
  BFormTextarea,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { toastSuccess, toastFailure } from '@/libs/toastification'
import { sweetWarning, sweetError } from '@/libs/sweet-alerts'
import PhoneNumberInput from '@/views/Components/Elements/PhoneNumberInput.vue'
import {
  buildFullPhone,
  normalizeDialCode,
} from '@/libs/phoneNumber'

export default {
  components: {
    PhoneNumberInput,
    BFormTextarea,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BFormFile,
    BImg,
    BForm,
    BSpinner,
  },
  data() {
    return {
      form: {
        name: null,
        email: null,
        phone: null,
        description: null,
        card_last_4: null,
      },
      pictureFile: null,
      picturePreview: '',
      customerDial: normalizeDialCode(window.companyPhoneCode) || '1',
      customerNumber: '',
      required,
      email,
      isCreateLoading: false,
    }
  },
  validations() {
    return {
      form: {
        name: { required },
        email: { required, email },
      },
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  methods: {
    ...mapActions({
      addCustomer: 'customers/addCustomer',
    }),
    generatePassword() {
      const lowercase = 'abcdefghjkmnopqrstuvwxyz'
      const uppercase = 'ABCDEFGHJKMNOPQRSTUVWXYZ'
      const numbers = '123456789'
      const symbols = '!@#$%^&*'
      const allChars = lowercase + uppercase + numbers + symbols
      let password = ''
      password += lowercase[Math.floor(Math.random() * lowercase.length)]
      password += uppercase[Math.floor(Math.random() * uppercase.length)]
      password += numbers[Math.floor(Math.random() * numbers.length)]
      password += symbols[Math.floor(Math.random() * symbols.length)]
      while (password.length < 8) {
        password += allChars[Math.floor(Math.random() * allChars.length)]
      }
      return password.split('').sort(() => Math.random() - 0.5).join('')
    },
    onPictureChange(event) {
      const file = event?.target?.files?.[0] || null
      this.pictureFile = file
      if (!file) {
        this.picturePreview = ''
        return
      }
      const reader = new FileReader()
      reader.onload = (loadEvent) => {
        this.picturePreview = loadEvent.target?.result || ''
      }
      reader.readAsDataURL(file)
    },
    showError(responseData = null, warning = false) {
      if (responseData != null && responseData.message != undefined && responseData.message != null) {
        let errorMSG = responseData.message + '</br>';
        if (responseData.errors) {
          Object.keys(responseData.errors).map(function (errorKey) {
            responseData.errors[errorKey].map(function (value) {
              errorMSG += value + '</br>';
            });
          });
        }
        if (warning) {
          sweetWarning('Warning', errorMSG);
        } else {
          sweetError('Error', errorMSG);
        }
      } else {
        toastFailure('Something went wrong while creating a customer.');
      }
    },
    async onSubmit(event) {
      event.preventDefault();
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) {
        return;
      }

      this.form.phone = String(this.customerNumber || '').trim()
        ? buildFullPhone(this.customerDial, this.customerNumber)
        : null;

      this.submitForm();
    },
    submitForm() {
      this.isCreateLoading = true

      const formData = new FormData()
      const appendIfPresent = (key, value) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value)
        }
      }
      appendIfPresent('name', this.form.name)
      appendIfPresent('email', this.form.email)
      appendIfPresent('phone', this.form.phone)
      appendIfPresent('description', this.form.description)
      appendIfPresent('card_last_4', this.form.card_last_4)
      formData.append('password', this.generatePassword())
      if (this.pictureFile instanceof File) {
        formData.append('picture', this.pictureFile)
      }

      this.addCustomer({ formData })
        .then((customer) => {
          if (customer?.email_sent === false) {
            const emailError = {
              message: 'Customer successfully created, but we couldn\'t send the email because of technical error.',
            };
            this.showError(emailError, 1);
          } else {
            toastSuccess('A new customer has been created');
          }

          this.$router.replace({ name: 'customers' })
        })
        .catch((error) => {
          this.showError(error.response?.data);
        })
        .finally(() => {
          this.isCreateLoading = false
        })
    },
  },
}
</script>
